import { api, type ApiEnvelope } from '@/lib/api-client';

/**
 * Suscripción a las notificaciones push — tarea 8.14
 *
 * ── NO SE PIDE EL PERMISO AL ENTRAR ────────────────────────────────────────
 *
 * Un cuadro de «¿permites notificaciones?» nada más abrir la aplicación se
 * rechaza casi siempre, y el navegador recuerda ese rechazo: a partir de ahí no
 * se puede volver a preguntar, ni desde un botón. Una petición mal puesta no es
 * una molestia, es perder la función para esa persona para siempre.
 *
 * Por eso se pide desde un botón explícito en el que la persona acaba de hacer
 * clic sabiendo lo que pide.
 *
 * ── TODO ESTO ES OPCIONAL Y NADA DE ESTO PUEDE ROMPER LA APLICACIÓN ────────
 *
 * Un navegador sin `PushManager`, sin service worker, sin contexto seguro o con
 * el permiso denegado devuelve un estado y ya está. No se lanza.
 */

export type EstadoDelPush =
  'no-soportado' | 'no-configurado' | 'sin-permiso' | 'denegado' | 'activo';

function soportado(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    window.isSecureContext
  );
}

/**
 * La clave viaja en base64url y `applicationServerKey` la quiere en bytes.
 *
 * SE DEVUELVE UN `ArrayBuffer` Y NO UN `Uint8Array`, y no es un capricho del
 * tipado: el `Uint8Array` de TypeScript puede estar respaldado por un
 * `SharedArrayBuffer`, que esta API no acepta. Devolver el búfer directamente
 * quita la ambigüedad.
 */
function aBytes(base64url: string): ArrayBuffer {
  const relleno = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + relleno).replace(/-/g, '+').replace(/_/g, '/');
  const bruto = window.atob(base64);
  const bytes = new Uint8Array(bruto.length);
  for (let i = 0; i < bruto.length; i++) bytes[i] = bruto.charCodeAt(i);
  return bytes.buffer;
}

export async function estadoDelPush(): Promise<EstadoDelPush> {
  if (!soportado()) return 'no-soportado';
  if (Notification.permission === 'denied') return 'denegado';

  const { data } = await api.get<ApiEnvelope<{ clave: string | null; disponible: boolean }>>(
    '/notifications/push/clave',
  );
  if (!data.disponible) return 'no-configurado';

  const registro = await navigator.serviceWorker.ready;
  const suscripcion = await registro.pushManager.getSubscription();

  if (suscripcion) return 'activo';
  return Notification.permission === 'granted' ? 'sin-permiso' : 'sin-permiso';
}

/**
 * Pide el permiso y registra este navegador.
 *
 * SE MANDA LA SUSCRIPCIÓN AL SERVIDOR ANTES DE DAR NADA POR HECHO. Si el
 * navegador la crea y el servidor no la guarda, el usuario cree que está
 * suscrito y no recibe nada — el peor de los estados, porque no se queja.
 */
export async function activarPush(): Promise<EstadoDelPush> {
  if (!soportado()) return 'no-soportado';

  const { data } = await api.get<ApiEnvelope<{ clave: string | null; disponible: boolean }>>(
    '/notifications/push/clave',
  );
  if (!data.disponible || !data.clave) return 'no-configurado';

  const permiso = await Notification.requestPermission();
  if (permiso !== 'granted') return permiso === 'denied' ? 'denegado' : 'sin-permiso';

  const registro = await navigator.serviceWorker.ready;

  const suscripcion =
    (await registro.pushManager.getSubscription()) ??
    (await registro.pushManager.subscribe({
      // OBLIGATORIO EN TODOS LOS NAVEGADORES ACTUALES. Sin esto, Chrome
      // rechaza la suscripción con un error que habla de «userVisibleOnly» y
      // no de lo que pasa: que se ha prometido que todo push se verá.
      userVisibleOnly: true,
      applicationServerKey: aBytes(data.clave),
    }));

  const json = suscripcion.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };

  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    return 'sin-permiso';
  }

  await api.post('/notifications/push/suscribir', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });

  return 'activo';
}

export async function desactivarPush(): Promise<EstadoDelPush> {
  if (!soportado()) return 'no-soportado';

  const registro = await navigator.serviceWorker.ready;
  const suscripcion = await registro.pushManager.getSubscription();

  if (!suscripcion) return 'sin-permiso';

  // PRIMERO EL SERVIDOR Y DESPUÉS EL NAVEGADOR. Al revés, si la llamada falla
  // el servidor seguiría mandando avisos a una suscripción que ya no existe, y
  // los iría acumulando hasta que el servicio de push la diera por muerta.
  await api.post('/notifications/push/desuscribir', { endpoint: suscripcion.endpoint });
  await suscripcion.unsubscribe();

  return 'sin-permiso';
}
